import React from 'react';
import { Phone, MessageSquare, ArrowRight, Mic, PhoneCall, Bot } from 'lucide-react';
import { agentTypes } from '../../data/mockData';

const AgentTypesSection = () => {
  return (
    <section className="bg-white py-24" id="solutions" aria-labelledby="solutions-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 id="solutions-heading" className="sr-only">AI Agent Solutions</h2>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Voice Agent Card */}
          <article className="group relative rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-lg transition-all duration-500">
            <div 
              className="absolute inset-0 opacity-30"
              aria-hidden="true"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }}
            />
            <div className="relative p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-purple-600" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                {agentTypes[0].title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {agentTypes[0].description}
              </p>
              
              {/* Professional Icon-based Visual */}
              <figure className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
                <div className="relative">
                  {/* Phone Mockup */}
                  <div className="w-40 h-72 bg-white rounded-3xl shadow-2xl border border-gray-200 p-2 transform group-hover:scale-105 transition-transform duration-500">
                    <div className="w-full h-full bg-gradient-to-b from-purple-500 to-purple-600 rounded-2xl flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                        <PhoneCall className="w-8 h-8 text-white" aria-hidden="true" />
                      </div>
                      <span className="text-white font-semibold text-lg">AI AutoBots</span>
                      <span className="text-white/80 text-sm mt-1">Connecting...</span>
                      <div className="flex gap-4 mt-6">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                          <Mic className="w-5 h-5 text-white" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Sound waves */}
                  <div className="absolute -right-8 top-1/2 -translate-y-1/2 flex flex-col gap-1" aria-hidden="true">
                    <div className="w-8 h-1 bg-purple-300 rounded-full animate-pulse" style={{animationDelay: '0ms'}} />
                    <div className="w-12 h-1 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '150ms'}} />
                    <div className="w-6 h-1 bg-purple-300 rounded-full animate-pulse" style={{animationDelay: '300ms'}} />
                    <div className="w-10 h-1 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '450ms'}} />
                    <div className="w-4 h-1 bg-purple-300 rounded-full animate-pulse" style={{animationDelay: '600ms'}} />
                  </div>
                </div>
                <figcaption className="sr-only">AI Voice Agent making automated phone calls</figcaption>
              </figure>
              
              <button className="mt-6 inline-flex items-center text-gray-600 hover:text-gray-900 group/btn" aria-label="Learn more about Voice Agents">
                <span>Learn more</span>
                <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" aria-hidden="true" />
              </button>
            </div>
          </article>

          {/* Chat Agent Card */}
          <article className="group relative rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-lg transition-all duration-500">
            <div 
              className="absolute inset-0 opacity-30"
              aria-hidden="true"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }}
            />
            <div className="relative p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                {agentTypes[1].title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {agentTypes[1].description}
              </p>
              
              {/* Professional Chat Visual */}
              <figure className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
                <div className="w-full max-w-xs transform group-hover:scale-105 transition-transform duration-500">
                  {/* Chat Widget Mockup */}
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">AI AutoBots</p>
                        <p className="text-white/80 text-xs">Online</p>
                      </div>
                    </div>
                    {/* Messages */}
                    <div className="p-4 space-y-3">
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]">
                          <p className="text-gray-700 text-sm">Hi! How can I help you today?</p>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-blue-500 rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%]">
                          <p className="text-white text-sm">I need support with my order</p>
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]">
                          <p className="text-gray-700 text-sm">Sure! Let me help you with that...</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <figcaption className="sr-only">AI Chat Agent providing customer support</figcaption>
              </figure>
              
              <button className="mt-6 inline-flex items-center text-gray-600 hover:text-gray-900 group/btn" aria-label="Learn more about Chat Agents">
                <span>Learn more</span>
                <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" aria-hidden="true" />
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default AgentTypesSection;
